import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { useUrlContext } from '../../../contexts/UrlContext/UrlContext';
import { generateRelativeDateFromUTCTimestamp } from '../../../app-kit/helpers/generateRelativeDateFromUTCTimestamp';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { PropertyCarousel } from '../../../app-kit/components/PropertyCarousel/PropertyCarousel';
import { MarketTrendsComparableProperties } from './subcomponents/MarketTrendsComparableProperties/MarketTrendsComparableProperties';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { mapBadgeVariant } from '../../../app-kit/helpers/mapBadgeVariant';
import { StyledProptrackLogoBrickSource } from '../../../components/ProptrackLogoBrickSource/ProptrackLogoBrickSource';

const marketTrendsHash = '#market-trends';
interface MarketTrendsBrickProps {
  title: string;
  subtitle: string;
}

const MarketTrendsBrick: FunctionComponent<PropsWithChildren<MarketTrendsBrickProps>> = ({
  title,
  subtitle,
  children,
}) => {
  const { hash } = useUrlContext();

  useEffect(() => {
    if (hash === marketTrendsHash) {
      document.getElementById('market-trends')?.scrollIntoView();
    }
  }, [hash]);

  return (
    <ContentBrick title={title} subtitle={subtitle} id="market-trends">
      {children}
    </ContentBrick>
  );
};

export const MarketTrendsBrickWithData: FunctionComponent = () => {
  const data = usePropertyPageQuery()?.data.propertyPageBySlugV3;

  if (!data?.marketTrends) return null;
  const { title, subtitle, comparableSoldProperties, otherSimilarSoldProperties } =
    data.marketTrends;

  return (
    <MarketTrendsBrick title={title} subtitle={subtitle}>
      {comparableSoldProperties && (
        <MarketTrendsComparableProperties
          description={comparableSoldProperties.description}
          factsHeader={comparableSoldProperties.factsHeader}
          showMoreButton={comparableSoldProperties.showMoreButton}
          items={comparableSoldProperties.items}
          otherSoldProperties={
            otherSimilarSoldProperties
              ? {
                  ...otherSimilarSoldProperties,
                  properties: otherSimilarSoldProperties?.properties.map(
                    ({
                      pathV2,
                      clickThroughTracking,
                      priceV2,
                      address,
                      image,
                      badgeV2,
                      features,
                      soldDate,
                    }) => ({
                      path: pathV2,
                      clickThroughSource: clickThroughTracking,
                      title: priceV2,
                      titleExtra: soldDate ? generateRelativeDateFromUTCTimestamp(soldDate) : null,
                      subtitle: address,
                      image,
                      badge: {
                        ...badgeV2,
                        variant: mapBadgeVariant(badgeV2.variant),
                      },
                      features: features?.map((feature) => ({
                        ...feature,
                        iconName: feature.iconNameV2,
                      })),
                    }),
                  ),
                }
              : null
          }
        />
      )}
      {!comparableSoldProperties && otherSimilarSoldProperties && (
        <PropertyCarousel
          properties={otherSimilarSoldProperties?.properties.map(
            ({
              pathV2,
              clickThroughTracking,
              priceV2,
              address,
              image,
              badgeV2,
              features,
              soldDate,
            }) => ({
              path: pathV2,
              clickThroughSource: clickThroughTracking,
              title: priceV2,
              titleExtra: soldDate ? generateRelativeDateFromUTCTimestamp(soldDate) : null,
              subtitle: address,
              image,
              badge: {
                ...badgeV2,
                variant: mapBadgeVariant(badgeV2.variant),
              },
              features: features?.map((feature) => ({ ...feature, iconName: feature.iconNameV2 })),
            }),
          )}
          tracking={otherSimilarSoldProperties.tracking?.showMoreProperties}
        />
      )}

      <StyledProptrackLogoBrickSource />
    </MarketTrendsBrick>
  );
};
